import React, { useState } from "react";
import { DatePicker, Space, Button, Typography, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useScreen } from "../Utils/Screens";

const { RangePicker } = DatePicker;
const { Text } = Typography;

/**
 * Generic Date Range Filter Component
 *
 * @param {Object} props
 * @param {Function} props.onFilterChange - Callback function that receives {startDate, endDate} or null when filter is reset
 * @param {string} props.apiEndpoint - API endpoint for fetching data
 * @param {Object} props.dateFormat - Format for displaying dates (default: 'YYYY-MM-DD')
 * @param {Object} props.defaultRange - Default date range {startDate, endDate} as dayjs objects
 * @param {boolean} props.showLabel - Whether to show the filter label (default: true)
 * @param {boolean} props.showAppliedRange - Whether to show the applied range text (default: true)
 * @param {string} props.labelText - Custom label text (default: 'Date Range')
 */
const DateRangeFilter = ({
  onFilterChange,
  dateFormat = "YYYY-MM-DD",
  defaultRange = null,
  showLabel = true,
  showAppliedRange = true,
  labelText = "Date Range",
}) => {
  const [dateRange, setDateRange] = useState(defaultRange);
  const [appliedRange, setAppliedRange] = useState(defaultRange);
  const [isLoading, setIsLoading] = useState(false);

  const isPhone = !useScreen("sm");

  // Generate preset options for common date ranges
  const presetRanges = {
    Today: [dayjs(), dayjs()],
    Yesterday: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
    "Last 7 Days": [dayjs().subtract(6, "day"), dayjs()],
    "Last 30 Days": [dayjs().subtract(29, "day"), dayjs()],
    "This Month": [dayjs().startOf("month"), dayjs().endOf("month")],
    "Last Month": [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
    // 'This Quarter': [dayjs().startOf('quarter'), dayjs().endOf('quarter')],
    "Year to Date": [dayjs().startOf("year"), dayjs()],
  };

  const handleRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleApplyFilter = async () => {
    if (!dateRange) return;

    setIsLoading(true);

    try {
      const formattedRange = {
        startDate: dateRange[0].format(dateFormat),
        endDate: dateRange[1].format(dateFormat),
      };

      // Update the applied range for display
      setAppliedRange(dateRange);

      // Call the parent component's filter function
      if (typeof onFilterChange === "function") {
        await onFilterChange(formattedRange);
      }
    } catch (error) {
      console.error("Error applying date filter:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilter = async () => {
    setDateRange(null);
    setAppliedRange(null);

    setIsLoading(true);
    try {
      // Call the parent component's filter function with null to reset
      if (typeof onFilterChange === "function") {
        await onFilterChange(null);
      }
    } catch (error) {
      console.error("Error clearing date filter:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <RangePicker
          className="min-w-64"
          value={dateRange}
          onChange={handleRangeChange}
          allowClear={true}
          format={dateFormat}
          presets={Object.entries(presetRanges).map(([label, dates]) => ({
            label,
            value: dates,
          }))}
        />
        {isPhone ? (
          <div style={{ marginTop: "10px" }}>
            <Space>
              <Button
                type="primary"
                onClick={handleApplyFilter}
                disabled={!dateRange}
                loading={isLoading}
              >
                Apply
              </Button>

              <Tooltip title="Reset filter">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleClearFilter}
                  disabled={(!dateRange && !appliedRange) || isLoading}
                />
              </Tooltip>
            </Space>
          </div>
        ) : (
          <Space>
            <Button
              type="primary"
              onClick={handleApplyFilter}
              disabled={!dateRange}
              loading={isLoading}
              style={{ marginLeft: "8px" }}
            >
              Apply
            </Button>

            <Tooltip title="Reset filter">
              <Button
                icon={<ReloadOutlined />}
                onClick={handleClearFilter}
                disabled={(!dateRange && !appliedRange) || isLoading}
              />
            </Tooltip>
          </Space>
        )}
      </div>

      {/* {showAppliedRange && appliedRange && (
          <Text type="secondary" className="mt-2 block">
            Filtered: {appliedRange[0].format(dateFormat)} to {appliedRange[1].format(dateFormat)}
          </Text>
        )} */}
    </>
    // <Card className="mb-4 shadow-sm">
    //   <Space direction="vertical" className="w-full">
    //     {showLabel && (
    //       <div className="flex items-center mb-2">
    //         <CalendarOutlined className="mr-2" />
    //         <Text strong>{labelText}</Text>
    //       </div>
    //     )}

    //     <div className="flex flex-wrap gap-2">
    //       <RangePicker
    //         className="min-w-64"
    //         value={dateRange}
    //         onChange={handleRangeChange}
    //         allowClear={true}
    //         format={dateFormat}
    //         presets={Object.entries(presetRanges).map(([label, dates]) => ({
    //           label,
    //           value: dates,
    //         }))}
    //       />

    //       <Space>
    //         <Button
    //           type="primary"
    //           onClick={handleApplyFilter}
    //           disabled={!dateRange}
    //           loading={isLoading}
    //         >
    //           Apply
    //         </Button>

    //         <Tooltip title="Reset filter">
    //           <Button
    //             icon={<ReloadOutlined />}
    //             onClick={handleClearFilter}
    //             disabled={(!dateRange && !appliedRange) || isLoading}
    //           />
    //         </Tooltip>
    //       </Space>
    //     </div>

    //     {showAppliedRange && appliedRange && (
    //       <Text type="secondary" className="mt-2 block">
    //         Filtered: {appliedRange[0].format(dateFormat)} to {appliedRange[1].format(dateFormat)}
    //       </Text>
    //     )}
    //   </Space>
    // </Card>
  );
};

export default DateRangeFilter;
